import React, { useState, useCallback, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import styled from "styled-components";

import { accountSchema } from "../validators/account";
import PlanCarousel from "../../../components/subscription/PlanCarousel";
import useCheckEmail from "../../../hook/Subscription/useCheckEmail";
import apiSubscription from "../../../services/subscription";

// ─── sessionStorage helpers (Phase 2 — existing user auth) ──────
//
// Persist the Bill_User auth token through page refresh so the user
// doesn't lose context if they accidentally reload mid-flow. We
// deliberately use sessionStorage (not localStorage) so closing the
// tab clears the token automatically — reduces leak surface.
//
// Storage shape:
//   { token, id_bill, id_user, id_project, first_name, email }
//
// The signup orchestrator (parent component) calls clearExistingAuth()
// after a successful POST /subscriptionV2/ — see Payment success
// handler.
const STORAGE_KEY = "subv2_existing_auth";

function readExistingAuth() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeExistingAuth(auth) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(auth));
  } catch {
    // Storage quota / privacy mode — silently ignore. The flow still
    // works for the duration of this page load via in-memory state.
  }
}

function clearExistingAuth() {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

/**
 * Step 2 — Owner account + plan selection.
 *
 * Email duplicate check:
 *   - useCheckEmail debounces both backend calls (Bill_User identity +
 *     project hr_email) for 500ms after the email field stops changing.
 *   - We block submission whenever the user can't proceed:
 *       billStatus === "existing_unverified" → tell user to verify in app
 *       projectError !== null                 → email used by another company
 *   - billStatus === "existing_verified" is NOT a block here — Phase 2
 *     will handle that by switching the form to login mode. For now we
 *     just surface a hint so the user knows the email is recognized.
 *
 * Layout:
 *  - Desktop: Form fills StepShell's fixed height; Body scrolls internally,
 *    Footer sticks to the bottom of the card.
 *  - Mobile: Form is auto-height; everything stacks naturally and the
 *    page itself scrolls.
 */
function AccountStep({
  id_project,
  // Display name for the SaaS being signed up for (e.g. "LeaveHub",
  // "Rentaru", "SmartLaw"). Used in field labels so the user knows
  // which app they're creating an account for. Optional — falls back
  // to a generic phrasing when not provided.
  projectName,
  defaultValues = {},
  onSubmit,
  onBack,
  onPlanChange,
  lang = "th",
}) {
  // ─── Phase 2 state — existing user auth (sessionStorage-backed) ──
  //
  // Restore on mount if the user just refreshed mid-flow. We only
  // re-hydrate when the email in storage matches what we expect — if
  // the user changed the email field after refresh, the old token is
  // stale and we drop it.
  const [existingAuth, setExistingAuthState] = useState(() => readExistingAuth());
  const [showLoginModal, setShowLoginModal] = useState(false);

  const setExistingAuth = useCallback((auth) => {
    setExistingAuthState(auth);
    if (auth) writeExistingAuth(auth);
    else clearExistingAuth();
  }, []);

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(accountSchema),
    defaultValues,
    mode: "onBlur",
  });

  // Watch both email fields so the check hook gets fresh values; the
  // hook does its own debouncing so cheap re-renders are fine.
  const emailValue        = watch("email");
  const projectEmailValue = watch("project_email");

  // Drop stale token if user changed Bill_User email after login.
  // Email is disabled while logged in, but a programmatic change
  // (DevTools, autofill quirks) shouldn't silently keep an invalid
  // session.
  useEffect(() => {
    if (existingAuth && emailValue && emailValue !== existingAuth.email) {
      setExistingAuth(null);
    }
  }, [emailValue, existingAuth, setExistingAuth]);

  // ─── Form value sync with existingAuth (Phase 2) ─────
  //
  // When the user logs in via the modal, we mirror the authenticated
  // email into the form's email field and lock it. Password stays
  // editable — the project user is a new account with its own
  // credentials, independent of Bill_User. The user picks a fresh
  // password for the SaaS app they're signing up for; they don't
  // have to reuse their invoize.me password.
  //
  // first_name / last_name stay editable too — /auth_user doesn't
  // return them and we don't want to force the user back to the
  // login modal to populate name fields.
  useEffect(() => {
    if (!existingAuth) return;
    setValue("email", existingAuth.email, { shouldValidate: false });
  }, [existingAuth, setValue]);

  const emailCheck = useCheckEmail({
    bill_email:    emailValue,
    project_email: projectEmailValue,
    id_project,
    // When user is logged in via existingAuth, the email is already
    // verified — skip the duplicate check (it would just confirm what
    // we already know and may flash "loading" / hint banners that
    // confuse the post-login UI).
    enabled: !existingAuth,
  });

  // Block submit only when there's a definite reason to. Schema
  // validation handles the rest. When user is logged in, we don't
  // gate on email checks — those run against the (now hidden) form
  // fields and the user has already proven identity via /auth_user.
  //
  // existing_verified is a special case: someone else already owns
  // this Bill_User. The current visitor must prove they're the owner
  // by signing in (LoginCTA below the email field) — otherwise we'd
  // be letting an unauthenticated stranger reuse the account just
  // because they typed the right email. Blocked until they sign in
  // and `existingAuth` is set.
  const emailBlocked =
    !existingAuth &&
    (emailCheck.billStatus === "existing_unverified" ||
     emailCheck.billStatus === "existing_verified" ||
     emailCheck.projectError !== null);

  // Wrap submit to inject the existing-user auth context. The parent
  // signup orchestrator passes `accessToken` to the API as the Bearer
  // header. The Bill_User identity (email) is locked from the login
  // session, but everything else — including the project user's
  // password — comes from the form, since the project account is a
  // brand new credential separate from the invoize.me login.
  const handleSubmitWithAuth = handleSubmit((formValues) => {
    if (existingAuth) {
      onSubmit({
        ...formValues,
        // Lock Bill_User email regardless of any stale form state.
        email: existingAuth.email,
        // Auth payload — orchestrator picks these up to build the
        // Authorization: Bearer header.
        existingAuth: {
          token:   existingAuth.token,
          id_bill: existingAuth.id_bill,
          id_user: existingAuth.id_user,
        },
      });
    } else {
      onSubmit(formValues);
    }
  });

  // Login modal — POST /auth_user, persist on success, surface error
  // inline. Local state lives in the modal component (below) but we
  // expose a callback for it to bubble the result up.
  const handleLoginSuccess = useCallback((authPayload) => {
    setExistingAuth(authPayload);
    setShowLoginModal(false);
    // Sync the form fields so any downstream reads see the right
    // values without waiting for re-render.
    setValue("email", authPayload.email, { shouldValidate: false });
  }, [setExistingAuth, setValue]);

  // Compose the message we surface under the email field. Server-side
  // strings are already localized (Thai), so we use them verbatim and
  // only synthesize a fallback when needed.
  // When user is logged in, suppress all email-check chrome — we
  // already trust the email and the post-login UI shows its own
  // status banner.
  const emailHelpText = (() => {
    if (existingAuth) return null;
    if (emailCheck.loading) return null; // suppress to avoid flashing
    if (emailCheck.error) return null;   // network errors handled separately
    // projectError lives under the project_email field now (Phase 2
    // split). Only Bill_User-side messages belong here.
    if (emailCheck.billStatus === "existing_unverified") {
      return { tone: "error", text: emailCheck.billMessage };
    }
    if (emailCheck.billStatus === "existing_verified") {
      // Two-line warning: identifies the existing account on line 1,
      // tells the user that signing in is REQUIRED to use this email
      // (we now block submit until they do — emailBlocked) on line 2.
      // The actual "Sign in" CTA is rendered just below as its own
      // button. Tone is "error" rather than "info" because the form
      // is currently un-submittable; we shouldn't dress that up as
      // a friendly suggestion.
      const recognized =
        lang === "en"
          ? `We recognize this account${
              emailCheck.billFirstName ? ` (${emailCheck.billFirstName})` : ""
            }.`
          : `บัญชีนี้มีอยู่ในระบบแล้ว${
              emailCheck.billFirstName ? ` (${emailCheck.billFirstName})` : ""
            }`;

      const explainer =
        lang === "en"
          ? "Sign in to confirm it's yours and link this project to your existing invoice records, or use a different email."
          : "กรุณาเข้าสู่ระบบเพื่อยืนยันตัวตน และเชื่อมโปรเจคนี้กับระบบจัดการใบเสร็จเดิม หรือใช้อีเมลอื่น";

      return {
        tone: "error",
        text: `${recognized}\n${explainer}`,
      };
    }
    return null;
  })();

  // Show the "click to login" CTA only when the email check tells us
  // it's a verified existing account. No need to nag if we're still
  // checking, the email is invalid, or the user is already logged in.
  const showLoginCTA =
    !existingAuth &&
    emailCheck.billStatus === "existing_verified" &&
    !emailCheck.loading;

  return (
    <Form onSubmit={handleSubmitWithAuth} noValidate>
      <Header>
        <Title>{lang === "en" ? "Your account & plan" : "บัญชีผู้ใช้งาน + แพ็กเกจ"}</Title>
        <Subtitle>
          {lang === "en"
            ? "Set your login credentials and confirm your plan."
            : "ตั้งบัญชี login และยืนยันแพ็กเกจของคุณ"}
        </Subtitle>
      </Header>

      <Body>
        <FieldGrid>
          <FieldCol>
            <Label htmlFor="acc-first">
              {lang === "en" ? "First name" : "ชื่อ"}
              <Required>*</Required>
            </Label>
            <Input
              id="acc-first"
              type="text"
              $hasError={!!errors.first_name}
              {...register("first_name")}
            />
            {errors.first_name && <ErrorText>{errors.first_name.message}</ErrorText>}
          </FieldCol>

          <FieldCol>
            <Label htmlFor="acc-last">
              {lang === "en" ? "Last name" : "นามสกุล"}
              <Required>*</Required>
            </Label>
            <Input
              id="acc-last"
              type="text"
              $hasError={!!errors.last_name}
              {...register("last_name")}
            />
            {errors.last_name && <ErrorText>{errors.last_name.message}</ErrorText>}
          </FieldCol>

          <FieldCol $fullWidth>
            <Label htmlFor="acc-email">
              {lang === "en"
                ? "invoize.me email (billing system)"
                : "อีเมลสำหรับ invoize.me (ระบบจัดการใบเสร็จ)"}
              <Required>*</Required>
              {!existingAuth && emailCheck.loading && (
                <CheckSpinner aria-label="checking">
                  {lang === "en" ? "checking…" : "กำลังตรวจสอบ…"}
                </CheckSpinner>
              )}
              {existingAuth && (
                <CheckSpinner style={{ color: "#10b981" }} aria-label="signed-in">
                  {lang === "en" ? "✓ signed in" : "✓ เข้าสู่ระบบแล้ว"}
                </CheckSpinner>
              )}
            </Label>
            <Input
              id="acc-email"
              type="email"
              autoComplete="email"
              disabled={!!existingAuth}
              $hasError={!existingAuth && (!!errors.email || emailBlocked)}
              {...register("email")}
            />
            {existingAuth ? (
              <SignedInRow>
                <SignedInText>
                  {lang === "en"
                    ? "You're signed in to invoize.me with this email."
                    : "เข้าสู่ระบบ invoize.me ด้วยอีเมลนี้แล้ว"}
                </SignedInText>
                <LinkButton
                  type="button"
                  onClick={() => setExistingAuth(null)}
                >
                  {lang === "en" ? "Sign out" : "ออกจากระบบ"}
                </LinkButton>
              </SignedInRow>
            ) : (
              <>
                {errors.email && <ErrorText>{errors.email.message}</ErrorText>}
                {!errors.email && emailHelpText && (
                  emailHelpText.tone === "error" ? (
                    <ErrorText>{emailHelpText.text}</ErrorText>
                  ) : (
                    <InfoText>{emailHelpText.text}</InfoText>
                  )
                )}
                {showLoginCTA && (
                  <LoginCTA
                    type="button"
                    onClick={() => setShowLoginModal(true)}
                  >
                    {/* Login / sign-in icon (Lucide log-in) — communicates
                        the action faster than the text arrow alone. */}
                    <svg
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      aria-hidden="true"
                    >
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                      <polyline points="10 17 15 12 10 7" />
                      <line x1="15" y1="12" x2="3" y2="12" />
                    </svg>
                    {lang === "en"
                      ? "Sign in to invoize.me"
                      : "เข้าสู่ระบบ invoize.me"}
                  </LoginCTA>
                )}
                {!errors.email && !emailHelpText && (
                  <Hint>
                    {lang === "en"
                      ? "Used to log into invoize.me and receive invoices."
                      : "ใช้สำหรับ login invoize.me และรับใบเสร็จ"}
                  </Hint>
                )}
              </>
            )}
          </FieldCol>

          <FieldCol $fullWidth>
            <Label htmlFor="acc-project-email">
              {lang === "en"
                ? `Login email for ${projectName || "the product"}`
                : `อีเมลสำหรับเข้าใช้งานระบบ${
                    projectName ? ` ${projectName}` : ""
                  }`}
              <Required>*</Required>
            </Label>
            <Input
              id="acc-project-email"
              type="email"
              autoComplete="email"
              $hasError={
                !!errors.project_email || !!emailCheck.projectError
              }
              {...register("project_email")}
            />
            {errors.project_email && (
              <ErrorText>{errors.project_email.message}</ErrorText>
            )}
            {!errors.project_email && emailCheck.projectError && (
              <ErrorText>{emailCheck.projectError.message}</ErrorText>
            )}
            {!errors.project_email && !emailCheck.projectError && (
              <Hint>
                {lang === "en"
                  ? "Used to log into the SaaS app you're signing up for. Can be the same as your invoize.me email."
                  : "ใช้สำหรับ login เข้าระบบที่กำลังสมัคร (สามารถใช้อีเมลเดียวกับ invoize.me ได้)"}
              </Hint>
            )}
          </FieldCol>

          <FieldCol>
            <Label htmlFor="acc-pwd">
              {lang === "en" ? "Password" : "รหัสผ่าน"}
              <Required>*</Required>
            </Label>
            <Input
              id="acc-pwd"
              type="password"
              autoComplete="new-password"
              $hasError={!!errors.password}
              {...register("password")}
            />
            {errors.password && <ErrorText>{errors.password.message}</ErrorText>}
          </FieldCol>

          <FieldCol>
            <Label htmlFor="acc-pwd2">
              {lang === "en" ? "Confirm password" : "ยืนยันรหัสผ่าน"}
              <Required>*</Required>
            </Label>
            <Input
              id="acc-pwd2"
              type="password"
              autoComplete="new-password"
              $hasError={!!errors.confirm_password}
              {...register("confirm_password")}
            />
            {errors.confirm_password && (
              <ErrorText>{errors.confirm_password.message}</ErrorText>
            )}
          </FieldCol>
        </FieldGrid>

        <Hint style={{ marginTop: 8 }}>
          {lang === "en"
            ? existingAuth
              ? "Set a password for this product account. It's separate from your invoize.me password."
              : "At least 8 characters, with one uppercase letter and one number."
            : existingAuth
              ? "ตั้งรหัสผ่านสำหรับเข้าใช้งานระบบที่กำลังสมัคร (แยกจากรหัสผ่าน invoize.me)"
              : "อย่างน้อย 8 ตัวอักษร, มีตัวพิมพ์ใหญ่และตัวเลข"}
        </Hint>

        <Divider />

        <SectionTitle>{lang === "en" ? "Choose your plan" : "เลือกแพ็กเกจ"}</SectionTitle>
        <SectionSub>
          {lang === "en"
            ? "You can change your plan before checkout."
            : "เปลี่ยนได้ก่อนชำระเงิน"}
        </SectionSub>

        <Controller
          name="id_subscription_plan_table"
          control={control}
          render={({ field, fieldState }) => (
            <>
              <PlanCarousel
                id_project={id_project}
                selectedId={field.value}
                onSelect={(plan) => {
                  field.onChange(plan.id_subscription_plan_table);
                  onPlanChange?.(plan);
                }}
                lang={lang}
              />
              {fieldState.error && (
                <ErrorText style={{ marginTop: 4 }}>
                  {fieldState.error.message}
                </ErrorText>
              )}
            </>
          )}
        />
      </Body>

      <Footer>
        <SecondaryButton type="button" onClick={onBack}>
          {lang === "en" ? "← Back" : "← ย้อนกลับ"}
        </SecondaryButton>
        <PrimaryButton
          type="submit"
          disabled={isSubmitting || emailBlocked || (!existingAuth && emailCheck.loading)}
        >
          {lang === "en" ? "Next →" : "ถัดไป →"}
        </PrimaryButton>
      </Footer>

      {showLoginModal && (
        <LoginModal
          defaultEmail={emailValue || ""}
          onSuccess={handleLoginSuccess}
          onClose={() => setShowLoginModal(false)}
          lang={lang}
        />
      )}
    </Form>
  );
}

export default AccountStep;

// ─── LoginModal — invoize.me sign-in (Phase 2) ──────────
//
// Self-contained: handles its own form state, calls /auth_user, and
// bubbles success up via onSuccess(authPayload). Closes on success
// (parent decides next render). On failure, surfaces the error
// inline and lets the user retry.

function LoginModal({ defaultEmail, onSuccess, onClose, lang }) {
  const [email, setEmail] = useState(defaultEmail);
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);

  // Modal is dismissible via the × button or the Cancel button only.
  // No Esc / backdrop-click — keeps the user from losing the modal
  // by an accidental click-outside while typing their password.

  const handleLogin = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setErrorMsg(null);

    if (!email || !password) {
      setErrorMsg(
        lang === "en"
          ? "Please enter both email and password."
          : "กรุณากรอกอีเมลและรหัสผ่าน"
      );
      return;
    }

    setSubmitting(true);
    try {
      const res = await apiSubscription.loginInvoize({ email, password });
      // Backend returns a top-level array — see services/subscription.js.
      const payload = Array.isArray(res) ? res[0] : res;

      if (!payload || payload.status !== true || !payload.accessToken) {
        setErrorMsg(
          lang === "en"
            ? "Invalid email or password."
            : "อีเมลหรือรหัสผ่านไม่ถูกต้อง"
        );
        return;
      }

      onSuccess({
        token:      payload.accessToken,
        id_bill:    payload.id_bill,
        id_user:    payload.id_user,
        id_project: payload.id_project,
        email,
        // /auth_user doesn't return first/last name — leave null;
        // they're collected separately in the form below.
        first_name: null,
        last_name:  null,
      });
    } catch (err) {
      console.error("[loginInvoize] failed:", err);
      setErrorMsg(
        lang === "en"
          ? "Sign-in failed. Please try again."
          : "เข้าสู่ระบบล้มเหลว กรุณาลองใหม่อีกครั้ง"
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ModalOverlay>
      <ModalDialog role="dialog" aria-modal="true">
        <ModalHeader>
          <ModalTitle>
            {lang === "en"
              ? "Sign in to invoize.me"
              : "เข้าสู่ระบบ invoize.me"}
          </ModalTitle>
          <ModalCloseButton
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            ×
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <ModalSubtitle>
            {lang === "en"
              ? "Use your existing invoize.me credentials to skip account creation."
              : "ใช้บัญชี invoize.me เดิมเพื่อข้ามขั้นตอนสมัครสมาชิก"}
          </ModalSubtitle>

          <FieldCol>
            <Label htmlFor="login-email">
              {lang === "en" ? "Email" : "อีเมล"}
              <Required>*</Required>
            </Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={submitting}
            />
          </FieldCol>

          <FieldCol style={{ marginTop: 12 }}>
            <Label htmlFor="login-password">
              {lang === "en" ? "Password" : "รหัสผ่าน"}
              <Required>*</Required>
            </Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={submitting}
            />
          </FieldCol>

          {errorMsg && <ErrorText style={{ marginTop: 8 }}>{errorMsg}</ErrorText>}
        </ModalBody>

        <ModalFooter>
          <SecondaryButton
            type="button"
            onClick={onClose}
            disabled={submitting}
          >
            {lang === "en" ? "Cancel" : "ยกเลิก"}
          </SecondaryButton>
          <PrimaryButton
            type="button"
            onClick={handleLogin}
            disabled={submitting || !email || !password}
          >
            {submitting
              ? lang === "en" ? "Signing in…" : "กำลังเข้าสู่ระบบ…"
              : lang === "en" ? "Sign in" : "เข้าสู่ระบบ"}
          </PrimaryButton>
        </ModalFooter>
      </ModalDialog>
    </ModalOverlay>
  );
}

// ─── Styled ──────────────────────────────────

const Form = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-width: 0;

  @media (max-width: 768px) {
    height: auto;
  }
`;

const Header = styled.div`
  padding: 24px 28px 12px;

  @media (max-width: 768px) {
    padding: 20px 20px 8px;
  }
`;

const Title = styled.h5`
  && {
    font-weight: 700;
    font-size: 18px;
    color: #111827;
    margin: 0 0 4px 0;
  }
`;

const Subtitle = styled.div`
  font-size: 13px;
  color: #6b7280;
`;

const Body = styled.div`
  padding: 12px 28px 16px;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  min-width: 0;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 3px;
  }

  @media (max-width: 768px) {
    flex: none;
    overflow: visible;
    padding: 12px 20px 16px;
  }
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FieldCol = styled.div`
  ${({ $fullWidth }) => $fullWidth && "grid-column: 1 / -1;"}
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const Label = styled.label`
  && {
    font-size: 12px;
    font-weight: 500;
    color: #374151;
    margin-bottom: 4px;
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

const Required = styled.span`
  color: #ef4444;
  margin-left: 4px;
`;

const CheckSpinner = styled.span`
  font-size: 11px;
  color: #6366f1;
  font-weight: 400;
  margin-left: auto;
`;

const Input = styled.input`
  width: 100%;
  height: 40px;
  font-size: 14px;
  font-weight: 400;
  color: #111827;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  outline: none;
  transition: border-color 0.15s, box-shadow 0.15s;
  font-family: inherit;
  box-sizing: border-box;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
  }

  &:disabled {
    background: #f9fafb;
    color: #6b7280;
    cursor: not-allowed;
    border-color: #e5e7eb;
  }

  ${({ $hasError }) =>
    $hasError &&
    `
    border-color: #ef4444;
    &:focus {
      border-color: #ef4444;
      box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.1);
    }
  `}
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 11px;
  margin-top: 4px;
  /* Honor explicit line breaks (\n) — used for the existing_verified
     warning which has two lines: the recognized account on top, the
     "sign in to confirm" instruction underneath. */
  white-space: pre-line;
  line-height: 1.5;
`;

const InfoText = styled.div`
  color: #4f46e5;
  font-size: 11px;
  margin-top: 4px;
  /* Honor explicit line breaks (\n) so multi-line info (e.g. the
     existing-account explainer) renders on two lines. */
  white-space: pre-line;
  line-height: 1.5;
`;

const Hint = styled.div`
  font-size: 11px;
  color: #9ca3af;
  margin-top: 4px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid #f1f3f5;
  margin: 24px 0;
`;

const SectionTitle = styled.h6`
  && {
    font-weight: 600;
    font-size: 15px;
    color: #111827;
    margin: 0 0 4px 0;
  }
`;

const SectionSub = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 12px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 16px 28px;
  border-top: 1px solid #f1f3f5;
  background: #ffffff;

  @media (max-width: 768px) {
    padding: 12px 20px;
  }
`;

const buttonBase = `
  height: 40px;
  padding: 0 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, border-color 0.15s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PrimaryButton = styled.button`
  && {
    ${buttonBase}
    background: #6366f1;
    color: #ffffff;

    &:hover:not(:disabled) {
      background: #4f46e5;
    }
  }
`;

const SecondaryButton = styled.button`
  && {
    ${buttonBase}
    background: #ffffff;
    color: #6b7280;
    border: 1px solid #d1d5db;

    &:hover:not(:disabled) {
      border-color: #9ca3af;
      color: #374151;
    }
  }
`;
// ─── Phase 2 styled components ──────────────────────────

// Row under the email field when the user is logged in via the
// modal. Compact, info-tone, with an inline "sign out" affordance.
const SignedInRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-top: 6px;
  padding: 8px 10px;
  background: #ecfdf5;
  border: 1px solid #a7f3d0;
  border-radius: 6px;
`;

const SignedInText = styled.div`
  font-size: 12px;
  color: #065f46;
  font-weight: 500;
  line-height: 1.4;
`;

const LinkButton = styled.button`
  background: none;
  border: none;
  color: #065f46;
  font-size: 12px;
  font-weight: 500;
  text-decoration: underline;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;

  &:hover:not(:disabled) {
    background: rgba(6, 95, 70, 0.08);
  }
`;

// "Sign in to invoize.me" CTA shown beneath the email field when an
// existing Bill_User is detected. Filled style — this is a primary
// action: the form is gated behind it (emailBlocked when
// existing_verified). Padded enough to register as a real button,
// not a text link.
const LoginCTA = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
  border: none;
  color: #ffffff;
  font-size: 13px;
  font-weight: 500;
  font-family: inherit;
  padding: 9px 16px;
  border-radius: 8px;
  cursor: pointer;
  margin-top: 8px;
  align-self: flex-start;
  box-shadow: 0 1px 2px rgba(99, 102, 241, 0.2);
  transition: transform 0.1s, box-shadow 0.15s;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
    transform: translateY(-1px);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 1px 2px rgba(99, 102, 241, 0.2);
  }

  &:focus-visible {
    outline: 2px solid #6366f1;
    outline-offset: 2px;
  }

  /* Login icon — sized to sit nicely with 13px text */
  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

// ─── Modal styled components ────────────────────────────

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 16px;
`;

const ModalDialog = styled.div`
  width: 100%;
  max-width: 420px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #f1f3f5;
`;

const ModalTitle = styled.h6`
  && {
    font-weight: 700;
    font-size: 16px;
    color: #111827;
    margin: 0;
  }
`;

const ModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  line-height: 1;
  color: #9ca3af;
  cursor: pointer;
  padding: 0;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #6b7280;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 16px 20px;
  overflow-y: auto;
`;

const ModalSubtitle = styled.div`
  font-size: 12px;
  color: #6b7280;
  margin-bottom: 16px;
  line-height: 1.5;
`;

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 20px;
  border-top: 1px solid #f1f3f5;
`;